import {
	DEADLINE_SITUATION_TO_URGENCY,
	INFRACTION_API_ORIGIN,
	INFRACTION_DEADLINE_SITUATION,
	INFRACTION_DEADLINE_TYPE,
} from "../constants/infraction-api-enums.constant";
import type { DeadlineUrgency } from "../types/infraction.types";
import type { InfractionListItemApiResponse } from "../types/infraction-api.types";

export const DEADLINE_NO_DATE_LABEL = "Sem data de indicação e/ou pagamento";

function formatShortDate(isoDate: string | null | undefined): string {
	if (!isoDate) {
		return "";
	}

	const [year, month, day] = isoDate.split("T")[0]?.split("-") ?? [];
	if (!(year && month && day)) {
		return "";
	}

	return `${day}/${month}`;
}

function formatDaysLabel(days: number): string {
	const absoluteDays = Math.abs(days);
	const unit = absoluteDays === 1 ? "dia" : "dias";
	return `${absoluteDays} ${unit}`;
}

export function getVehicleLabel(
	brand: string | null,
	model: string | null
): string {
	const parts = [brand, model].filter(Boolean);
	if (parts.length === 0) {
		return "—";
	}

	return parts.join(" ");
}

function hasDeadlineReferenceDates(
	item: InfractionListItemApiResponse
): boolean {
	return Boolean(
		item.deadline?.date ?? item.driverIndicationDeadline ?? item.dueDate
	);
}

export function isDeadlineFallback(
	item: InfractionListItemApiResponse
): boolean {
	const deadline = item.deadline;
	const hasStructuredDeadline = Boolean(
		deadline && deadline.type !== INFRACTION_DEADLINE_TYPE.NONE
	);

	if (hasStructuredDeadline) {
		return false;
	}

	return !hasDeadlineReferenceDates(item);
}

function buildPaidDeadlineText(
	daysFromToday: number | null,
	situationLabel: string | null,
	typeLabel: string | null
): string {
	if (daysFromToday === 0) {
		return "Paga hoje";
	}

	if (daysFromToday !== null && daysFromToday !== undefined) {
		return `Paga há ${formatDaysLabel(daysFromToday)}`;
	}

	return situationLabel ?? typeLabel ?? "Paga";
}

function buildActiveDeadlineText(
	typeLabel: string,
	situation: number,
	daysFromToday: number | null,
	situationLabel: string | null
): string {
	switch (situation) {
		case INFRACTION_DEADLINE_SITUATION.EXPIRED: {
			if (daysFromToday !== null && daysFromToday !== undefined) {
				return `${typeLabel} vencido há ${formatDaysLabel(daysFromToday)}`;
			}
			return situationLabel ?? `${typeLabel} vencido`;
		}
		case INFRACTION_DEADLINE_SITUATION.DUE_TODAY:
			return `${typeLabel} vence hoje`;
		case INFRACTION_DEADLINE_SITUATION.UPCOMING: {
			if (daysFromToday !== null && daysFromToday !== undefined) {
				return `${typeLabel} em ${formatDaysLabel(daysFromToday)}`;
			}
			return situationLabel ?? typeLabel;
		}
		case INFRACTION_DEADLINE_SITUATION.COMPLETED:
		case INFRACTION_DEADLINE_SITUATION.CANCELLED:
			return situationLabel ?? typeLabel;
		default:
			return situationLabel ?? typeLabel;
	}
}

export function getDeadlinePrimaryText(
	item: InfractionListItemApiResponse
): string {
	const deadline = item.deadline;
	if (!deadline || deadline.type === INFRACTION_DEADLINE_TYPE.NONE) {
		if (isDeadlineFallback(item)) {
			return DEADLINE_NO_DATE_LABEL;
		}

		return "—";
	}

	if (deadline.type === INFRACTION_DEADLINE_TYPE.PAID) {
		return buildPaidDeadlineText(
			deadline.daysFromToday,
			deadline.deadlineSituationLabel,
			deadline.deadlineTypeLabel
		);
	}

	if (deadline.type === INFRACTION_DEADLINE_TYPE.CANCELLED) {
		return deadline.deadlineSituationLabel ?? deadline.deadlineTypeLabel ?? "—";
	}

	const typeLabel = deadline.deadlineTypeLabel ?? "Prazo";

	return buildActiveDeadlineText(
		typeLabel,
		deadline.situation,
		deadline.daysFromToday,
		deadline.deadlineSituationLabel
	);
}

export function getDeadlineDateShort(
	item: InfractionListItemApiResponse
): string {
	const deadline = item.deadline;

	if (deadline?.type === INFRACTION_DEADLINE_TYPE.PAID) {
		return formatShortDate(item.paymentDate ?? deadline.date);
	}

	const deadlineDate =
		deadline?.date ?? item.driverIndicationDeadline ?? item.dueDate;
	return formatShortDate(deadlineDate);
}

export function getDeadlineUrgency(
	item: InfractionListItemApiResponse
): DeadlineUrgency {
	const situation = item.deadline?.situation;
	if (situation === undefined || situation === null) {
		return "normal";
	}

	return DEADLINE_SITUATION_TO_URGENCY[situation] ?? "normal";
}

export function getListItemStatusLabel(
	item: InfractionListItemApiResponse
): string {
	return item.displayStatusLabel ?? item.statusLabel ?? "—";
}

export function getInfractionOriginTooltipLabel(
	item: InfractionListItemApiResponse
): string {
	if (item.origin === INFRACTION_API_ORIGIN.MANUAL) {
		return "Cadastrada manualmente";
	}

	return "Cadastrada via sistema";
}

export function isManualInfractionOrigin(
	item: InfractionListItemApiResponse
): boolean {
	return item.origin === INFRACTION_API_ORIGIN.MANUAL;
}
